import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, usersAPI } from '../services/api';
import * as webauthn from '../utils/webauthn';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      const storedToken = sessionStorage.getItem('token');
      const storedUser = sessionStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          const response = await usersAPI.getMe();
          // /users/me returns an envelope ({ user, profile, activity counts… });
          // the auth user is the inner record — same shape the login response
          // carries. Storing the envelope made role/name read undefined after a
          // refresh, so admins lost the admin link and /admin bounced them.
          const me = response.data && response.data.user ? response.data.user : response.data;
          setUser(me);
          try { sessionStorage.setItem('user', JSON.stringify(me)); } catch { /* noop */ }
          setToken(storedToken);
        } catch (error) {
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    restoreSession();
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const response = await authAPI.login({ email, password });
      const { access_token: newToken, user: userData } = response.data;

      sessionStorage.setItem('token', newToken);
      sessionStorage.setItem('user', JSON.stringify(userData));
      setToken(newToken);
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d.message || String(d)).join('; ')
        : detail || error.response?.data?.message || 'Login failed. Please try again.';
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Passkey (WebAuthn) login — backend + helpers shipped with the security port.
  const loginWithPasskey = async (email) => {
    setLoading(true);
    try {
      const options = (await authAPI.webauthnLoginBegin(email)).data;
      const credential = await webauthn.getAssertion(options);
      const response = await authAPI.webauthnLoginComplete(email, credential);
      const { access_token: newToken, user: userData } = response.data;

      sessionStorage.setItem('token', newToken);
      sessionStorage.setItem('user', JSON.stringify(userData));
      setToken(newToken);
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      // NotAllowedError = user cancelled the browser passkey prompt.
      if (error?.name === 'NotAllowedError') {
        return { success: false, error: 'Passkey sign-in was cancelled.' };
      }
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d.message || String(d)).join('; ')
        : detail || error.response?.data?.message || 'Passkey sign-in failed. Please try again.';
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // `birthYear` added 2026-09-18 (KAN-491). The backend has always accepted it — the schema
  // comment reads "the application/signup form is expected to send it" — and nothing ever did,
  // so every account was created with no age. Optional here so callers that do not have one
  // (a learner arriving at /register directly) behave exactly as before.
  const register = async (name, email, password, inviteCode, birthYear = null) => {
    setLoading(true);
    try {
      const response = await authAPI.register({
        name,
        email,
        password,
        invite_code: inviteCode,
        ...(birthYear ? { birth_year: birthYear } : {}),
      });
      const { access_token: newToken, user: userData } = response.data;

      sessionStorage.setItem('token', newToken);
      sessionStorage.setItem('user', JSON.stringify(userData));
      setToken(newToken);
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d.message || String(d)).join('; ')
        : detail || error.response?.data?.message || 'Registration failed. Please try again.';
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  // EditProfilePage has destructured `updateUser` from this context since the repository's
  // first commit, and the context has never provided it. Saving a profile therefore always
  // threw "updateUser is not a function" — minified in production to "r is not a function".
  //
  // The throw happens AFTER users.updateProfile() resolves, so the save itself succeeded
  // every time; only the client-side refresh failed. The page then showed an error toast
  // and stayed put, so the user was told their change was lost when it had actually been
  // written. That is the worst shape this bug could take: it trains people to distrust a
  // save that worked, or to retry it.
  //
  // Refreshing the cached user matters beyond silencing the error — the header and any
  // other consumer read from here, so without it a renamed user keeps the old name until
  // they log out. Tolerates a missing payload rather than clobbering the session with
  // undefined.
  const updateUser = (userData) => {
    if (!userData) return user;
    setUser(userData);
    sessionStorage.setItem('user', JSON.stringify(userData));
    return userData;
  };

  const isAuthenticated = !!token && !!user;
  const isAdmin = user?.role === 'admin';
  // editor drafts content in the Content desk; admin does everything an editor can plus approve/manage.
  const isEditor = user?.role === 'editor' || isAdmin;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        isAuthenticated,
        isAdmin,
        isEditor,
        login,
        loginWithPasskey,
        register,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
